import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AuthContext } from "../state/AuthContext";

const permissionTemplate = [
  { role: "Admin", access: "Dashboard, Master, Entry, Report, Settings" },
  { role: "Team Member", access: "Dashboard, Entry, Report" },
];

export default function SettingsPage() {
  const { http } = useContext(AuthContext);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // User form state
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    roleCode: "team_member",
    isActive: true,
  });

  // Excel Upload state
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadError, setUploadError] = useState(null);

  const canSave = useMemo(() => {
    return form.name.trim() && form.email.trim() && form.password.trim() && form.roleCode;
  }, [form]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [rolesRes, usersRes] = await Promise.all([
        http.get("/api/master/roles"),
        http.get("/api/admin/users"),
      ]);
      setRoles(rolesRes.data.items.filter((item) => item.code !== "customer"));
      setUsers(usersRes.data.items);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load settings.");
    } finally {
      setLoading(false);
    }
  }, [http]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await http.post("/api/admin/users", form);
      setSuccess("User created successfully.");
      setForm({
        name: "",
        email: "",
        password: "",
        roleCode: "team_member",
        isActive: true,
      });
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to create user.");
    } finally {
      setSaving(false);
    }
  };

  const downloadSampleTemplate = () => {
    const csvContent =
      "Full Name,Mobile Number,Address,Email,Aadhar No,PAN No,Birth Date\n" +
      'Ramesh Sharma,9876543210,"123 M.G. Road, Pune",ramesh@example.com,123456789012,ABCDE1234F,1990-05-15\n' +
      'Sunita Patel,9812345678,"45 Station Road, Mumbai",sunita@example.com,987654321098,XYZPS9876K,1995-11-20\n';

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "sample_customers_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      setUploadError("Please select an Excel (.xlsx, .xls) or .csv file first.");
      return;
    }

    setUploading(true);
    setUploadError(null);
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      const res = await http.post("/api/settings/import-customers", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setUploadResult(res.data);
      setSelectedFile(null);
      // Reset file input
      const fileInput = document.getElementById("excelCustomerFileInput");
      if (fileInput) fileInput.value = "";
    } catch (err) {
      setUploadError(err?.response?.data?.message || "Failed to import customers file.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="d-grid gap-3">
      <div>
        <h4 className="mb-1">User Settings & Excel Upload ⚙️</h4>
        <p className="text-muted mb-0">
          Create system users, bulk import customer lists via Excel, and manage permissions.
        </p>
      </div>

      {error ? <div className="alert alert-danger">{error}</div> : null}
      {success ? <div className="alert alert-success">{success}</div> : null}

      <div className="row g-3">
        {/* Excel Customer Upload */}
        <div className="col-12 col-xl-6">
          <div className="card shadow-sm h-100 border-start border-primary border-4">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0 fw-bold">Excel Upload Customer List 📊</h6>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={downloadSampleTemplate}
                >
                  📥 Sample Template
                </button>
              </div>

              <p className="text-muted small mb-3">
                Bulk import customer records using Excel (<code>.xlsx</code>, <code>.xls</code>) or <code>.csv</code> format.
                Existing customers (matched by mobile) will automatically be updated.
              </p>

              {uploadError ? <div className="alert alert-danger py-2 small">{uploadError}</div> : null}

              {uploadResult ? (
                <div className="alert alert-success py-2">
                  <div className="fw-bold mb-1">{uploadResult.message}</div>
                  <div className="small">
                    ✅ New Created: <strong>{uploadResult.importedCount}</strong> | 🔄 Updated: <strong>{uploadResult.updatedCount}</strong> | ⚠️ Skipped: <strong>{uploadResult.skippedCount}</strong>
                  </div>
                  {uploadResult.errors && uploadResult.errors.length > 0 ? (
                    <div className="mt-2 text-danger small">
                      <strong>Errors / Warnings:</strong>
                      <ul className="mb-0 ps-3">
                        {uploadResult.errors.slice(0, 5).map((errStr, idx) => (
                          <li key={idx}>{errStr}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </div>
              ) : null}

              <form onSubmit={handleFileUpload} className="d-grid gap-3">
                <div>
                  <label className="form-label fw-semibold">Select Customer Excel / CSV File</label>
                  <input
                    id="excelCustomerFileInput"
                    type="file"
                    className="form-control"
                    accept=".xlsx, .xls, .csv"
                    onChange={(e) => setSelectedFile(e.target.files[0] || null)}
                  />
                </div>

                <div className="d-flex justify-content-end">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={!selectedFile || uploading}
                  >
                    {uploading ? "Importing Data..." : "🚀 Upload & Import Customers"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Create User */}
        <div className="col-12 col-xl-6">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <h6 className="mb-3 fw-bold">Create User 👤</h6>
              <form className="row g-3" onSubmit={onSubmit}>
                <div className="col-12 col-md-6">
                  <label className="form-label">Name</label>
                  <input
                    className="form-control"
                    value={form.name}
                    onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  />
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label">Email</label>
                  <input
                    className="form-control"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                  />
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label">Password</label>
                  <input
                    className="form-control"
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                  />
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label">Role</label>
                  <select
                    className="form-select"
                    value={form.roleCode}
                    onChange={(e) => setForm((p) => ({ ...p, roleCode: e.target.value }))}
                  >
                    {roles.map((role) => (
                      <option key={role.id} value={role.code}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-12">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="isActive"
                      checked={form.isActive}
                      onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))}
                    />
                    <label className="form-check-label" htmlFor="isActive">
                      Active User
                    </label>
                  </div>
                </div>
                <div className="col-12 d-flex justify-content-end">
                  <button className="btn btn-primary" type="submit" disabled={!canSave || saving}>
                    {saving ? "Saving..." : "Create User"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-3">
        {/* User Permissions Overview */}
        <div className="col-12 col-xl-6">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <h6 className="mb-3 fw-bold">User Permission Overview</h6>
              <div className="list-group list-group-flush">
                {permissionTemplate.map((item) => (
                  <div key={item.role} className="list-group-item px-0">
                    <div className="fw-semibold">{item.role}</div>
                    <div className="text-muted small">{item.access}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Existing Users Table */}
        <div className="col-12 col-xl-6">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0 fw-bold">System Users</h6>
                <span className="text-muted small">
                  {loading ? "Loading..." : `${users.length} user(s)`}
                </span>
              </div>
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!loading && users.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="text-muted">
                          No users found.
                        </td>
                      </tr>
                    ) : null}
                    {users.map((user) => (
                      <tr key={user.id}>
                        <td>{user.name}</td>
                        <td>{user.email}</td>
                        <td>{user.roleName}</td>
                        <td>
                          {user.isActive ? (
                            <span className="badge text-bg-success">Active</span>
                          ) : (
                            <span className="badge text-bg-secondary">Inactive</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
